import { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMasters } from "@/lib/masters";
import { PageHeader, Card, Btn, Chip, Select, Field } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { ArrowLeft, UploadSimple, FileText } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { istDateString } from "@/lib/date";

// Field candidates the user can map to
const TARGETS = [
  { key: "filename", label: "Filename (full)" },
  { key: "job_name", label: "Job Name" },
  { key: "client", label: "Client" },
  { key: "dpi", label: "DPI" },
  { key: "plate_size", label: "Plate Size" },
  { key: "sets", label: "Sets" },
  { key: "colours", label: "Colours" },
  { key: "set_colour", label: "Set/Colour (e.g. 5S2C)" },
  { key: "operator", label: "Operator" },
  { key: "machine", label: "Machine" },
  { key: "remark", label: "Remark" },
  { key: "_ignore", label: "— ignore —" },
];

// Auto-detect target based on header name
function autoMap(header) {
  const h = (header || "").toString().trim().toLowerCase();
  if (!h) return "_ignore";
  if (/(file\s*name|filename)/.test(h)) return "filename";
  if (/(job\s*name|name)$/.test(h) || h === "job") return "job_name";
  if (h === "client" || /customer|party/.test(h)) return "client";
  if (h === "dpi") return "dpi";
  if (/plate.*size|size$/.test(h)) return "plate_size";
  if (h === "sets" || h === "s") return "sets";
  if (h === "colours" || h === "colors" || h === "c") return "colours";
  if (/set.*col|s\/c|s\.?c/.test(h)) return "set_colour";
  if (h === "operator") return "operator";
  if (h === "machine") return "machine";
  if (h === "remark" || h === "remarks" || h === "notes") return "remark";
  return "_ignore";
}

export default function BulkImport() {
  const m = useMasters();
  const nav = useNavigate();
  const today = istDateString();
  const [text, setText] = useState("");
  const [sheet, setSheet] = useState(null); // { headers, rows }
  const [mapping, setMapping] = useState({}); // header -> target
  const [rows, setRows] = useState([]); // editable rows with resolved ids
  const [defaults, setDefaults] = useState({ job_date: today, production_unit_id: "", location_id: "" });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const first = wb.SheetNames[0];
      const ws = wb.Sheets[first];
      const arr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (arr.length < 2) return toast.error("File has no data rows.");
      const headers = arr[0].map(h => String(h).trim());
      const data = arr.slice(1).filter(r => r.some(c => String(c).trim() !== ""));
      setSheet({ headers, rows: data });
      // auto-map
      const map = {};
      headers.forEach(h => { map[h] = autoMap(h); });
      setMapping(map);
      toast.success(`Loaded "${first}" — ${data.length} rows, ${headers.length} columns.`);
    } catch (err) { toast.error("Could not read file: " + err.message); }
    finally { setBusy(false); }
  };

  const analyseText = async () => {
    const filenames = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (filenames.length === 0) return toast.error("Paste at least one filename per line.");
    setBusy(true); setResults(null); setSheet(null);
    try {
      const { data } = await api.post("/jobs/parse_filenames_bulk", { filenames });
      buildRowsFromParse(data.results, filenames);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const analyseSheet = async () => {
    if (!sheet) return toast.error("Upload a file first.");
    setBusy(true); setResults(null);
    // Build column-to-target lookup, invert
    const targets = {};
    Object.entries(mapping).forEach(([h, t]) => { if (t !== "_ignore") targets[t] = h; });

    // If a filename column exists and no explicit job_name column, parse via filenames endpoint
    const hasFilename = !!targets.filename;
    const hasExplicit = ["job_name", "client", "plate_size", "machine"].some(k => targets[k]);

    try {
      if (hasFilename && !hasExplicit) {
        const filenames = sheet.rows.map(r => String(r[sheet.headers.indexOf(targets.filename)] || "").trim());
        const { data } = await api.post("/jobs/parse_filenames_bulk", { filenames });
        buildRowsFromParse(data.results, filenames);
      } else {
        // Explicit columns via parse_rows
        const explicitRows = sheet.rows.map(r => {
          const obj = {};
          Object.entries(targets).forEach(([target, header]) => {
            const idx = sheet.headers.indexOf(header);
            if (idx >= 0) obj[target] = String(r[idx] ?? "").trim();
          });
          return obj;
        });
        const { data } = await api.post("/jobs/parse_rows", { rows: explicitRows });
        buildRowsFromParse(data.results, explicitRows.map((r, i) => r.filename || `row_${i+1}`));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const buildRowsFromParse = (results, labels) => {
    const built = results.map((r, i) => ({
      idx: i,
      filename: labels[i] || `row_${i+1}`,
      parse: r,
      job_name: r.fields?.job_name?.value || "",
      client_id: r.fields?.client?.id || "",
      plate_size_id: r.fields?.plate_size?.id || "",
      machine_id: r.fields?.machine?.id || "",
      operator_id: r.fields?.operator?.id || "",
      sets: r.fields?.set_colour?.sets || 1,
      colours: r.fields?.set_colour?.colours || 1,
      dpi: r.fields?.dpi?.value || "",
      remarks: r.fields?.remark?.value || "",
      as_draft: !(r.overall?.ok && r.fields?.plate_size?.id && r.fields?.machine?.id),
    }));
    setRows(built);
    toast.success(`Parsed ${built.length} rows.`);
  };

  const isReady = (r, statusIfDraft) => {
    const base = r.job_name && r.client_id && r.sets > 0 && r.colours > 0 && defaults.production_unit_id && defaults.location_id;
    if (!base) return false;
    // Submit needs plate size + machine + location. Draft still needs plate + machine to book stock later.
    return r.plate_size_id && r.machine_id;
  };

  const readyCount = rows.filter(isReady).length;
  const draftCount = rows.filter(r => isReady(r) && r.as_draft).length;

  const commit = async () => {
    if (!defaults.production_unit_id || !defaults.location_id) {
      return toast.error("Set default Production Unit and Location for the batch.");
    }
    const jobs = rows.filter(isReady).map(r => ({
      job_date: defaults.job_date,
      job_name: r.job_name,
      file_name: r.filename,
      client_id: r.client_id,
      dpi: r.dpi || null,
      plate_size_id: r.plate_size_id,
      sets: parseInt(r.sets), colours: parseInt(r.colours),
      operator_id: r.operator_id || null,
      machine_id: r.machine_id,
      production_unit_id: defaults.production_unit_id,
      location_id: defaults.location_id,
      remarks: r.remarks || null,
      status: r.as_draft ? "draft" : "submitted",
    }));
    if (jobs.length === 0) return toast.error("No rows are ready to submit.");
    const submitCount = jobs.filter(j => j.status === "submitted").length;
    if (!window.confirm(`Create ${jobs.length} job(s)? ${submitCount} will deduct stock now, ${jobs.length - submitCount} as draft.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk_create", { jobs, stop_on_error: false });
      setResults(data);
      toast.success(`Created ${data.created} of ${data.total}. ${data.failed>0?data.failed+' failed.':''}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateRow = (idx, patch) => setRows(rs => rs.map(r => r.idx === idx ? { ...r, ...patch } : r));

  // Aggregate unique "not_found" master items across all rows so admin can add them in one go
  const missingMasters = useMemo(() => {
    const clients = new Map();      // raw -> count
    const plate_sizes = new Map();  // canonical -> count
    const dpis = new Map();         // value -> count
    const operators = new Map();    // raw -> count
    for (const r of rows) {
      const F = r.parse?.fields || {};
      if (F.client?.status === "not_found" && F.client.raw) {
        clients.set(F.client.raw, (clients.get(F.client.raw) || 0) + 1);
      }
      if (F.plate_size?.status === "not_found" && F.plate_size.value && /^\d+X\d+$/.test(F.plate_size.value)) {
        plate_sizes.set(F.plate_size.value, (plate_sizes.get(F.plate_size.value) || 0) + 1);
      }
      if (F.dpi?.status === "not_found" && F.dpi.value) {
        dpis.set(F.dpi.value, (dpis.get(F.dpi.value) || 0) + 1);
      }
      if (F.operator?.status === "not_found" && F.operator.raw) {
        operators.set(F.operator.raw, (operators.get(F.operator.raw) || 0) + 1);
      }
    }
    return {
      clients: [...clients.entries()],
      plate_sizes: [...plate_sizes.entries()],
      dpis: [...dpis.entries()],
      operators: [...operators.entries()],
    };
  }, [rows]);

  const missingTotal = missingMasters.clients.length + missingMasters.plate_sizes.length + missingMasters.dpis.length + missingMasters.operators.length;

  const reAnalyseAfterMasterAdd = async () => {
    await m.reload();
    // Re-run the same parse to refresh statuses
    if (sheet) return analyseSheet();
    if (text.trim()) return analyseText();
  };

  const addOneMaster = async (kind, raw) => {
    let confirmMsg = "";
    let call = null;
    if (kind === "client") {
      confirmMsg = `Add "${raw}" to Client Master?`;
      call = () => api.post("/masters/clients", { name: raw, active: true, credit_limit: 0 });
    } else if (kind === "plate_size") {
      const match = raw.match(/^(\d+)X(\d+)$/);
      if (!match) return toast.error(`"${raw}" is not a valid plate size`);
      const w = parseInt(match[1]); const h = parseInt(match[2]);
      confirmMsg = `Add plate size "${w}X${h}" to Master?`;
      call = () => api.post("/masters/plate_sizes", { width: w, height: h, unit: "mm", active: true, min_stock: 0 });
    } else if (kind === "dpi") {
      const v = parseInt(raw);
      confirmMsg = `Add DPI ${v} to Master?`;
      call = () => api.post("/masters/dpis", { value: v, active: true });
    } else if (kind === "operator") {
      confirmMsg = `Add operator "${raw}" to Master?`;
      call = () => api.post("/masters/operators", { name: raw, active: true });
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      await call();
      toast.success("Added to master. Re-analysing rows…");
      await reAnalyseAfterMasterAdd();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addAllMissingMasters = async () => {
    if (missingTotal === 0) return;
    const parts = [];
    if (missingMasters.plate_sizes.length) parts.push(`${missingMasters.plate_sizes.length} plate size(s)`);
    if (missingMasters.dpis.length) parts.push(`${missingMasters.dpis.length} DPI value(s)`);
    if (missingMasters.operators.length) parts.push(`${missingMasters.operators.length} operator(s)`);
    if (missingMasters.clients.length) parts.push(`${missingMasters.clients.length} client(s)`);
    if (!window.confirm(`Add ${parts.join(", ")} to their respective masters?`)) return;
    setBusy(true);
    try {
      // Clients
      if (missingMasters.clients.length) {
        await api.post("/masters/clients/bulk-import", {
          rows: missingMasters.clients.map(([raw]) => ({ name: raw, active: true, credit_limit: 0 })),
        });
      }
      // Plate sizes
      if (missingMasters.plate_sizes.length) {
        const rows = missingMasters.plate_sizes.map(([canonical]) => {
          const [w, h] = canonical.split("X").map(x => parseInt(x));
          return { width: w, height: h, unit: "mm", min_stock: 0, active: true };
        });
        await api.post("/masters/plate_sizes/bulk-import", { rows });
      }
      // DPIs
      if (missingMasters.dpis.length) {
        await api.post("/masters/dpis/bulk-import", {
          rows: missingMasters.dpis.map(([raw]) => ({ value: parseInt(raw), active: true })),
        });
      }
      // Operators
      if (missingMasters.operators.length) {
        await api.post("/masters/operators/bulk-import", {
          rows: missingMasters.operators.map(([raw]) => ({ name: raw, active: true })),
        });
      }
      toast.success("Masters added. Re-analysing rows…");
      await reAnalyseAfterMasterAdd();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="bulk-import-page">
      <Toaster position="bottom-right" richColors />
      <PageHeader
        title="Bulk Import Jobs"
        subtitle="Paste filenames or upload CSV/XLSX. Each row is validated against Master Data before commit."
        actions={<Btn variant="outline" onClick={()=>nav("/jobs")}><ArrowLeft size={14} /> Back to Job Register</Btn>}
      />

      <div className="p-6 space-y-4">
        <Card>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Default Job Date*">
                <input type="date" value={defaults.job_date} onChange={e=>setDefaults({...defaults, job_date: e.target.value})}
                  className="border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm" data-testid="bulk-default-date" />
              </Field>
              <Field label="Default Production Unit*">
                <Select value={defaults.production_unit_id} onChange={e=>setDefaults({...defaults, production_unit_id: e.target.value})} data-testid="bulk-default-pu">
                  <option value="">— Select —</option>
                  {m.production_units.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
              <Field label="Default Location*">
                <Select value={defaults.location_id} onChange={e=>setDefaults({...defaults, location_id: e.target.value})} data-testid="bulk-default-loc">
                  <option value="">— Select —</option>
                  {m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-zinc-200">
              {/* Left: paste text */}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Option 1 — Paste filenames (one per line)</div>
                <textarea
                  value={text}
                  onChange={e=>setText(e.target.value)}
                  rows={6}
                  placeholder="Diwali-Card_Acme_2400_550X700_5S2C_Ramesh_AMSKY_Rush.pdf&#10;Menu-Card_Bharat_2400_410X510_3S4C_Suresh_CRON.pdf"
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono"
                  data-testid="bulk-textarea"
                />
                <div className="flex justify-end mt-2">
                  <Btn onClick={analyseText} disabled={busy} data-testid="bulk-analyse-btn">Analyse Filenames</Btn>
                </div>
              </div>

              {/* Right: file upload */}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Option 2 — Upload CSV or XLSX</div>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-zinc-300 rounded-sm px-4 py-6 cursor-pointer hover:border-zinc-500 transition-colors bg-zinc-50/50" data-testid="bulk-file-drop">
                  <UploadSimple size={28} className="text-zinc-400" />
                  <div className="mt-2 text-sm font-semibold">{sheet ? `Loaded: ${sheet.rows.length} rows` : "Click to select file"}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Accepts .csv, .xlsx, .xls</div>
                  <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
                </label>
                {sheet && (
                  <div className="mt-3 border border-zinc-200 rounded-sm p-3 bg-white">
                    <div className="text-xs font-semibold text-zinc-700 mb-2 flex items-center gap-1"><FileText size={12} /> Column mapping</div>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {sheet.headers.map(h => (
                        <div key={h} className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-zinc-600 truncate flex-1" title={h}>{h}</span>
                          <span className="text-zinc-400">→</span>
                          <Select value={mapping[h] || "_ignore"} onChange={e=>setMapping({...mapping, [h]: e.target.value})} className="text-xs min-w-0" data-testid={`bulk-map-${h}`}>
                            {TARGETS.map(t=><option key={t.key} value={t.key}>{t.label}</option>)}
                          </Select>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end mt-3">
                      <Btn onClick={analyseSheet} disabled={busy} data-testid="bulk-analyse-sheet-btn">Analyse Sheet</Btn>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {rows.length > 0 && missingTotal > 0 && (
          <Card>
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between bg-amber-50/50">
              <div className="font-heading font-bold text-sm uppercase tracking-wider text-amber-900">
                Missing masters — {missingTotal} unique item{missingTotal===1?"":"s"} not in Master Data
              </div>
              <Btn variant="primary" onClick={addAllMissingMasters} disabled={busy} data-testid="bulk-add-all-masters">
                Add all {missingTotal} to masters
              </Btn>
            </div>
            <div className="p-4 space-y-3 text-sm">
              {missingMasters.plate_sizes.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-1">Plate sizes ({missingMasters.plate_sizes.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {missingMasters.plate_sizes.map(([val, count]) => (
                      <button key={val} onClick={() => addOneMaster("plate_size", val)} className="text-xs font-mono border border-zinc-300 rounded-sm px-2 py-1 hover:border-sky-500 hover:bg-sky-50" data-testid={`bulk-add-plate-${val}`}>
                        + {val} <span className="text-zinc-500">×{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {missingMasters.dpis.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-1">DPIs ({missingMasters.dpis.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {missingMasters.dpis.map(([val, count]) => (
                      <button key={val} onClick={() => addOneMaster("dpi", val)} className="text-xs font-mono border border-zinc-300 rounded-sm px-2 py-1 hover:border-sky-500 hover:bg-sky-50" data-testid={`bulk-add-dpi-${val}`}>
                        + {val} <span className="text-zinc-500">×{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {missingMasters.operators.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-1">Operators ({missingMasters.operators.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {missingMasters.operators.map(([val, count]) => (
                      <button key={val} onClick={() => addOneMaster("operator", val)} className="text-xs border border-zinc-300 rounded-sm px-2 py-1 hover:border-sky-500 hover:bg-sky-50" data-testid={`bulk-add-operator-${val}`}>
                        + {val} <span className="text-zinc-500">×{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {missingMasters.clients.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-1">Clients ({missingMasters.clients.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {missingMasters.clients.map(([val, count]) => (
                      <button key={val} onClick={() => addOneMaster("client", val)} className="text-xs border border-zinc-300 rounded-sm px-2 py-1 hover:border-sky-500 hover:bg-sky-50" data-testid={`bulk-add-client-${val}`}>
                        + {val} <span className="text-zinc-500">×{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-zinc-500 pt-1">Tip: click <span className="font-semibold">Add all</span> to add every missing item in one shot, then the rows will re-analyse and turn green.</div>
            </div>
          </Card>
        )}

        {rows.length > 0 && (
          <Card>
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-heading font-bold text-sm uppercase tracking-wider">
                Parsed Rows — {readyCount}/{rows.length} ready
                {draftCount > 0 && <span className="ml-2 text-xs font-normal text-amber-700">({draftCount} will be saved as draft)</span>}
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-zinc-500">Tick "Draft" to skip stock consumption on any row.</span>
                <Btn variant="primary" onClick={commit} disabled={busy || readyCount===0} data-testid="bulk-commit-btn">
                  Create {readyCount} Jobs
                </Btn>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full data-table" data-testid="bulk-table">
                <thead><tr>
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Ready</th>
                  <th className="px-2 py-2 text-center">Draft</th>
                  <th className="px-2 py-2 text-left">Source</th>
                  <th className="px-2 py-2 text-left">Job Name</th>
                  <th className="px-2 py-2 text-left">Client</th>
                  <th className="px-2 py-2 text-left">Machine</th>
                  <th className="px-2 py-2 text-left">Size</th>
                  <th className="px-2 py-2 text-center">S/C</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-left">Problems</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => {
                    const ready = isReady(r);
                    const problems = [
                      ...(r.parse?.overall?.errors || []),
                      ...(r.job_name ? [] : ["Job name missing"]),
                      ...(r.client_id ? [] : ["Client not resolved"]),
                      ...(r.plate_size_id ? [] : ["Plate size not resolved"]),
                      ...(r.machine_id ? [] : ["Machine not resolved"]),
                    ];
                    return (
                      <tr key={r.idx} data-testid={`bulk-row-${r.idx}`} className={ready ? "" : "bg-red-50/40"}>
                        <td className="px-2 py-2 font-mono text-[11px]">{r.idx+1}</td>
                        <td className="px-2 py-2">{ready ? <Chip tone="good">Ready</Chip> : <Chip tone="bad">Blocked</Chip>}</td>
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox" checked={!!r.as_draft} onChange={e=>updateRow(r.idx,{as_draft:e.target.checked})} data-testid={`bulk-draft-${r.idx}`} />
                        </td>
                        <td className="px-2 py-2 font-mono text-[11px] max-w-[160px] truncate" title={r.filename}>{r.filename}</td>
                        <td className="px-2 py-2">
                          <input value={r.job_name} onChange={e=>updateRow(r.idx,{job_name:e.target.value})} className="border border-zinc-300 rounded-sm px-1.5 py-0.5 text-xs w-40" />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={r.client_id} onChange={e=>updateRow(r.idx,{client_id:e.target.value})} className="text-xs">
                            <option value="">—</option>
                            {m.clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={r.machine_id} onChange={e=>updateRow(r.idx,{machine_id:e.target.value})} className="text-xs">
                            <option value="">—</option>
                            {m.machines.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={r.plate_size_id} onChange={e=>updateRow(r.idx,{plate_size_id:e.target.value})} className="text-xs">
                            <option value="">—</option>
                            {m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                          </Select>
                        </td>
                        <td className="px-2 py-2 text-center font-mono text-xs">
                          <input type="number" min="1" value={r.sets} onChange={e=>updateRow(r.idx,{sets:e.target.value})} className="border border-zinc-300 rounded-sm px-1 py-0.5 text-xs w-12" />
                          S
                          <input type="number" min="1" value={r.colours} onChange={e=>updateRow(r.idx,{colours:e.target.value})} className="border border-zinc-300 rounded-sm px-1 py-0.5 text-xs w-12 ml-1" />
                          C
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-bold">{(parseInt(r.sets)||0)*(parseInt(r.colours)||0)}</td>
                        <td className="px-2 py-2 text-[11px] text-red-700 max-w-[240px]">
                          {problems.length === 0 ? <span className="text-emerald-700">—</span> : problems.slice(0,3).join(" · ")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {results && (
          <Card testid="bulk-results">
            <div className="px-4 py-3 border-b border-zinc-200 font-heading font-bold text-sm uppercase tracking-wider">Results</div>
            <div className="p-4 space-y-2">
              <div className="flex gap-4">
                <Chip tone="good">Created: {results.created}</Chip>
                <Chip tone={results.failed>0?"bad":"neutral"}>Failed: {results.failed}</Chip>
                <Chip tone="info">Total: {results.total}</Chip>
              </div>
              {results.results.filter(r=>!r.ok).length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-zinc-500 mb-1">Failures:</div>
                  <ul className="text-[12px] space-y-1">
                    {results.results.filter(r=>!r.ok).map(r => (
                      <li key={r.index} className="text-red-700">
                        Row {r.index+1}: {typeof r.error === "object" ? `${r.error.problem} — ${r.error.resolution}` : String(r.error)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
